import { Search } from 'lucide-preact';
import { PropertyExplorerTable } from '../../components/overview';
import { BlockInfoIcon, Card, EmptyState, LoadingState } from '../../components/ui';
import { getOverviewBlockSubtitle, getOverviewBlockTitle } from '../../content';
import { useFilter } from '../../contexts/FilterContext';
import { writeRouteToWindow } from '../../routing';
import { usePropertyExplorer } from './usePropertyExplorer';
import type { PeriodMode } from '../../functions/calculations';
import type { ClientOverview, Unit } from '../../types/api';
import '../../css/propertyExplorer.css';

interface OverviewPropertyExplorerSectionProps {
  overviewData: ClientOverview[];
  allUnits: Unit[];
  periodMode: PeriodMode;
}

export function OverviewPropertyExplorerSection({
  overviewData,
  allUnits,
  periodMode,
}: OverviewPropertyExplorerSectionProps) {
  const { clientUnits, setUnit } = useFilter();
  const explorer = usePropertyExplorer({ overviewData, allUnits, periodMode });

  const handleSelectProperty = (unitId: number) => {
    const clientUnit = clientUnits.find((unit) => unit.unit_id_no === unitId);
    if (!clientUnit) return;

    setUnit(clientUnit);
    writeRouteToWindow('unit-overview');
  };

  const hasRows = explorer.visibleRows.length > 0;
  const isFilteredEmpty = !hasRows && explorer.rows.length > 0;

  return (
    <Card padding="flush" className="property-explorer-card">
      <header className="property-explorer-card__header">
        <div className="property-explorer-card__heading">
          <div className="overview-panel-card__title-row">
            <h2 className="overview-panel-card__title">
              {getOverviewBlockTitle('propertyExplorer', 'Property Explorer')}
            </h2>
            <BlockInfoIcon blockId="propertyExplorer" />
          </div>
          <p className="overview-panel-card__subtitle">
            {explorer.periodCaption
              ? `NOI, cash flow and CoC cover ${explorer.periodCaption}`
              : getOverviewBlockSubtitle('propertyExplorer', 'Per-property performance detail')}
          </p>
        </div>

        <label className="property-explorer__search">
          <Search size={15} aria-hidden="true" />
          <input
            type="search"
            value={explorer.searchTerm}
            placeholder="Search properties…"
            aria-label="Search properties"
            onInput={(event) =>
              explorer.setSearchTerm((event.target as HTMLInputElement).value)
            }
          />
        </label>
      </header>

      <div className="property-explorer-card__body">
        {explorer.loading ? (
          <LoadingState message="Loading property details…" />
        ) : explorer.error ? (
          <EmptyState message={explorer.error} />
        ) : isFilteredEmpty ? (
          <EmptyState message={`No properties match “${explorer.searchTerm}”.`} />
        ) : !hasRows ? (
          <EmptyState message="No properties available for this client." />
        ) : (
          <PropertyExplorerTable
            rows={explorer.visibleRows}
            currencyCode={explorer.currencyCode}
            onSelectProperty={handleSelectProperty}
          />
        )}
      </div>
    </Card>
  );
}
